import type { AxiosInstance } from 'axios';
import {
  TUTORIAL_CONNECTION_ID,
  TUTORIAL_ORG_ID,
  TUTORIAL_PROVIDER_ID,
  TUTORIAL_ROUTE_ID,
  TUTORIAL_ROUTE_RECORD_ID,
  tutorialConnection,
  tutorialEmptyList,
  tutorialProviderIntegration,
  tutorialRoute,
  tutorialRouteRecord,
} from './tutorialData';

// GUNDI-5665 (TEMPORARY, ≤3 weeks): feeds the REAL Route/Connection pages mock
// data while the tour is on their screens. Installed by createAxiosInstance.
// Its isTutorialActive flag is also read by src/analytics/analytics.ts, which
// drops GA pageviews/events while the tour runs — those two imports are the
// only tutorial code outside src/components/tutorial/.
//
// REMOVAL MANIFEST (delete the tutorial in one commit):
//   1. delete src/components/tutorial/ (and src/__test__/components/tutorial/)
//   2. src/api/apiUtils.tsx — remove the installTutorialInterceptor import + call
//   2b. src/analytics/analytics.ts — remove the isTutorialActive import and the
//       ReactGA gating shim (rename ReactGA4 back to ReactGA)
//   3. src/components/common/NotificationsMenu.tsx — remove the notification
//      with its "Take a tour" button and the useTutorial import
//   4. src/NewApp.tsx — remove TutorialProvider / TutorialOverlay / TutorialStartModal
//   5. translations: remove the "tutorial." keys and the tutorial UI strings from EN/ES/FR
//   6. optional — added FOR the tour but harmless and convention-following, can stay:
//      data-testids RouteFlowMap-container, RouteFlowMap-add-destination-button,
//      ConnectionDetails-{configuration,details}-tab, AccountNode-{provider,destination}-div,
//      AddDestinationDrawer-{candidate-row,candidate-checkbox,attach-button},
//      ConfigurationsTab-view-connection-button, and the nav_*_label spans in
//      src/components/common/Nav.tsx.
//
// The flag is module-level, so a page refresh disarms the interceptor at the
// same moment it ends the tour. Nothing to clean up.
let tutorialActive = false;
// The tour's one piece of mock STATE: whether the guided add-destination step
// has run. The mock Route starts destination-less, the tour simulates attaching
// Tutorial EarthRanger, and every later response reflects that.
let destinationAttached = false;
export function setTutorialActive(active: boolean) {
  tutorialActive = active;
  if (active) destinationAttached = false; // every tour starts from scratch
}
export function isTutorialActive() {
  return tutorialActive;
}

const TUTORIAL_IDS = [
  TUTORIAL_ROUTE_ID,
  TUTORIAL_CONNECTION_ID,
  TUTORIAL_PROVIDER_ID,
  TUTORIAL_ROUTE_RECORD_ID,
  TUTORIAL_ORG_ID,
];

// The flow map's destination picker (getDestinationsList) carries NO tutorial
// id — it is a plain "all destinations" list. While the tour is running it is
// claimed anyway so the drawer shows exactly one deterministic row (the tour's
// EarthRanger); the overlay blocks all real interaction, so nothing else can
// depend on the real list mid-tour.
const DESTINATION_PICKER_PREFIX = '/v2/integrations/?action_type=push';

export function installTutorialInterceptor(instance: AxiosInstance) {
  instance.interceptors.request.use((config) => {
    const url = config.url ?? '';
    // A tutorial id reaches us either in the path or — for the list
    // endpoints, which take axios `params` — in the query object. Matching
    // on the URL alone missed /v2/routes/?{provider|destination}=… and let
    // those calls through to the real API.
    const params = config.params ? JSON.stringify(config.params) : '';
    const mentionsTutorialId = TUTORIAL_IDS.some(
      (id) => url.includes(id) || params.includes(id),
    );
    const isDestinationPicker = url.startsWith(DESTINATION_PICKER_PREFIX);
    if (!tutorialActive || !(mentionsTutorialId || isDestinationPicker))
      return config;
    const method = (config.method ?? 'get').toLowerCase();
    // Answer locally: the adapter short-circuits the network entirely, so no
    // tutorial id ever reaches the real API.
    config.adapter = async () => ({
      data: mockResponseFor(url + params, method),
      status: 200,
      statusText: 'OK',
      headers: { 'content-type': 'application/json' },
      // REQUIRED, not decoration: apiUtils' response interceptor treats a
      // responseURL that does not start with the base URL as a redirect to
      // Keycloak and shows "Your session has expired". Without this, a
      // faked response falls back to config.url (a RELATIVE path), which
      // never matches — it killed the tour on its first mocked request.
      request: { responseURL: `${config.baseURL ?? ''}${url}` },
      config,
    });
    return config;
  });
}

// True when the request asks for routes where `id` plays `role` — either as a
// query-string pair (?role=id) or as a JSON-serialised axios param ("role":"id").
function matchesRoleQuery(
  urlWithParams: string,
  role: string,
  id: string,
): boolean {
  return (
    urlWithParams.includes(`${role}=${id}`) ||
    urlWithParams.includes(`"${role}":"${id}"`)
  );
}

// The route/record with the destination attached — or not yet, depending on
// how far the guided add-destination step has come.
function currentRoute() {
  return {
    ...tutorialRoute,
    destinations: destinationAttached ? [tutorialConnection] : [],
  };
}
function currentRouteRecord() {
  return {
    ...tutorialRouteRecord,
    destinations: destinationAttached ? [tutorialConnection] : [],
  };
}

function mockResponseFor(url: string, method: string): unknown {
  // The guided attach: the drawer PATCHes the route record with the selected
  // destination ids. Flip the flag — every response from here on shows the
  // destination as part of the Route.
  if (method === 'patch' && url.includes(TUTORIAL_ROUTE_RECORD_ID)) {
    destinationAttached = true;
    return currentRouteRecord();
  }
  // The drawer's candidate list: exactly one row, the tour's EarthRanger.
  if (url.startsWith(DESTINATION_PICKER_PREFIX)) {
    return { ...tutorialEmptyList, results: [tutorialConnection], count: 1 };
  }
  // Collections first: these carry a tutorial id as a QUERY param, so the
  // "/{id}/" detail matches below must not claim them.
  if (url.includes('/v2/logs/')) return tutorialEmptyList;
  if (url.includes('/v2/sources/')) return tutorialEmptyList;
  // getRoutesByIntegration: /v2/connections/?provider=… | ?destination_id=…
  if (url.includes('/v2/connections/?'))
    return { ...tutorialEmptyList, results: [currentRoute()], count: 1 };
  // Role lookups must answer by ROLE, not just by id. The tutorial
  // EarthRanger is a destination ONLY: if /v2/routes/?provider=<connection>
  // returned the record, ManageDestinationDetails' provider-route redirect
  // would bounce /connections/<id> to /routes/<record> (a page no fixture
  // covers) and the Connection page would also grow a bogus "Sending data
  // to" table. Both happened; this branch is the fix.
  if (matchesRoleQuery(url, 'provider', TUTORIAL_CONNECTION_ID))
    return tutorialEmptyList;
  // Route-record lists: /v2/routes/ (getRoutesByIntegration, listRoutes) —
  // the id rides in axios params, appended to `url` by the caller above.
  if (url.includes('/v2/routes/?') || url.startsWith('/v2/routes/{')) {
    return { ...tutorialEmptyList, results: [currentRouteRecord()], count: 1 };
  }
  // getIntegrationIdsWithActionType: /v2/integrations/?id__in=…&action_type=…
  // Answering with an EMPTY list is deliberate — it means "neither side
  // registers that action", which keeps the tour's reverse-direction toggles
  // off and the flow map simple.
  if (url.includes('/v2/integrations/?')) return tutorialEmptyList;

  // The Route itself — /v2/connections/{route_id}/ (getRoute, getRouteDetails).
  if (url.includes(`/v2/connections/${TUTORIAL_ROUTE_ID}`))
    return currentRoute();
  // The route RECORD — /v2/routes/{record_id}/ (getRouteConfiguration).
  if (url.includes(`/v2/routes/${TUTORIAL_ROUTE_RECORD_ID}`))
    return currentRouteRecord();
  // Integration details — /v2/integrations/{id}/ for the provider and the
  // destination; the api-key sub-resource answers with a fake key.
  if (url.includes('api-key'))
    return { api_key: 'tutorial-api-key-not-a-real-credential' };
  if (url.includes(TUTORIAL_PROVIDER_ID)) return tutorialProviderIntegration;
  if (url.includes(TUTORIAL_CONNECTION_ID)) return tutorialConnection;
  if (url.includes(TUTORIAL_ROUTE_ID)) return currentRoute();

  if (process.env.NODE_ENV !== 'production') {
    // Any sub-resource the fixtures do not cover yet shows up here.
    console.warn('[tutorial] unmatched request:', url);
  }
  return tutorialEmptyList;
}
